import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAssignedForms, fetchForms } from 'store/Forms/actions'
import FormContainer from './FormContainer'
import TakeFormModal from './TakeFormModal'
import ViewSubmissionModal from './ViewSubmissionModal'
import FiltersModal from './FiltersModal'
import { deleteForm } from 'store/Forms/actions'
import AssignFormUsersModal from './AssignFormUsersModal'
import SettingsModal from './SettingsModal'
import CreateEditForm from './CreateEditForm'
import CreateEditSection from './CreateEditSection'
import ScheduledSubmissionsModal from './ScheduledSubmissionsModal'

function EmployeeForms({
  searchFilter,
  activeFilter,
  showFiltersModal,
  setShowFiltersModal,
  setEmployeeFormsLength,
  selectedSectionFilter,
  onSectionClick,
  autoFillFormId,
  removeFillFormFromUrl,
}) {
  const dispatch = useDispatch()
  const { assignedForms, forms } = useSelector((store) => store.form)
  const [showTakeFormModal, setShowTakeFormModal] = useState(false)
  const [showViewSubmissionModal, setShowViewSubmissionModal] = useState(false)

  const [showEditFormModal, setShowEditFormModal] = useState(false)
  const [showEditSectionModal, setShowEditSectionModal] = useState(false)
  const [showFormSettingsModal, setShowFormSettingsModal] = useState(false)
  const [showFormSubmissionsModal, setShowFormSubmissionsModal] =
    useState(false)

  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false)
  const [filteredForms, setFilteredForms] = useState(assignedForms || [])

  const [formId, setFormId] = useState(null)
  const [selectedSection, setSelectedSection] = useState(null)

  const deleteFormFn = (formId) => {
    // Look in both assigned forms and sections
    const itemToDelete =
      assignedForms?.find((item) => item.id === formId) ||
      forms?.find((item) => item.id === formId)
    const isSection = itemToDelete?.is_section

    const confirmMessage = isSection
      ? 'Are you sure you want to delete this section? All forms in this section will be moved to no section!'
      : 'Are you sure you want to delete this form? All its submissions will be deleted too!'

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(confirmMessage)) {
      return
    }
    dispatch(deleteForm(formId))

    // Update the filtered forms by removing the deleted item
    setFilteredForms((prev) => prev?.filter((form) => form.id !== formId))
  }

  const handleEditClick = (form) => {
    if (form.is_section) {
      setSelectedSection(form)
      setShowEditSectionModal(true)
    } else {
      setFormId(form.id)
      setShowEditFormModal(true)
    }
  }

  useEffect(() => {
    dispatch(fetchAssignedForms())
    dispatch(fetchForms()) // Also fetch forms to get sections

    return () => {
      dispatch(fetchAssignedForms())
      dispatch(fetchForms())
    }
  }, [dispatch])

  useEffect(() => {
    if (filteredForms) setEmployeeFormsLength(filteredForms?.length)
  }, [filteredForms, setEmployeeFormsLength])

  useEffect(() => {
    // Get sections that are assigned to the employee
    const assignedSections =
      assignedForms?.filter((form) => form.is_section) || []
    const assignedSectionIds = assignedSections.map((section) => section.id)

    // Get forms that belong to assigned sections
    const formsFromAssignedSections =
      forms?.filter(
        (form) =>
          !form.is_section && assignedSectionIds.includes(form.section_id)
      ) || []

    // Get all accessible items (assigned forms + forms from assigned sections)
    const accessibleForms = [
      ...(assignedForms?.filter((form) => !form.is_section) || []), // Only assigned forms (not sections)
      ...formsFromAssignedSections, // Forms from assigned sections
    ]

    // Get section IDs that are needed for navigation (sections that contain accessible forms)
    const neededSectionIds = new Set([
      ...assignedSectionIds, // Directly assigned sections
      ...accessibleForms
        .filter((form) => form.section_id)
        .map((form) => form.section_id), // Parent sections of accessible forms
    ])

    // Get only the sections that are needed for navigation
    const neededSections =
      forms?.filter(
        (form) => form.is_section && neededSectionIds.has(form.id)
      ) || []

    // Combine all items: assigned sections + needed sections + accessible forms
    const allItems = [
      ...assignedSections, // Directly assigned sections
      ...neededSections, // Parent sections needed for navigation
      ...accessibleForms, // All accessible forms
    ]

    // Remove duplicates (in case sections appear multiple times)
    const uniqueItems = allItems.filter(
      (item, index, self) => index === self.findIndex((i) => i.id === item.id)
    )

    if (uniqueItems && uniqueItems.length) {
      let filtered = uniqueItems.filter((form) =>
        form?.title?.toLowerCase()?.includes(searchFilter?.toLowerCase())
      )

      // Apply section filter
      if (selectedSectionFilter) {
        // Show only assigned forms and sub-sections belonging to the selected section
        filtered = filtered.filter((form) => {
          return form.section_id === selectedSectionFilter
        })
      } else {
        // Show only top-level items: top-level sections and assigned forms with no section
        filtered = filtered.filter((form) => {
          return !form.section_id // This will show both top-level sections and ungrouped forms
        })
      }

      setFilteredForms(filtered)
    } else {
      setFilteredForms([])
    }
  }, [assignedForms, forms, searchFilter, selectedSectionFilter])

  useEffect(() => {
    if (filteredForms) setEmployeeFormsLength(filteredForms?.length)
  }, [filteredForms, setEmployeeFormsLength])

  return (
    <>
      <div className='d-flex flex-column'>
        {filteredForms && filteredForms.length
          ? filteredForms.map((form, index) => (
              <FormContainer
                key={form.id}
                form={form}
                setFormId={setFormId}
                setShowEditFormModal={setShowEditFormModal}
                setShowAssignUsersModal={setShowAssignUsersModal}
                setShowFormSettingsModal={setShowFormSettingsModal}
                setShowFormSubmissionsModal={setShowFormSubmissionsModal}
                deleteForm={deleteFormFn}
                lastForm={index === filteredForms.length - 1}
                onSectionClick={onSectionClick}
                onEditClick={() => handleEditClick(form)}
                autoFillFormId={autoFillFormId}
                removeFillFormFromUrl={removeFillFormFromUrl}
              />
            ))
          : null}
      </div>
      {showAssignUsersModal && (
        <AssignFormUsersModal
          setShowAssignUsersModal={setShowAssignUsersModal}
          showAssignUsersModal={showAssignUsersModal}
          formId={formId}
        />
      )}
      {showFormSettingsModal && (
        <SettingsModal
          setShowModal={setShowFormSettingsModal}
          showModal={showFormSettingsModal}
          formId={formId}
        />
      )}
      {showFormSubmissionsModal && (
        <ScheduledSubmissionsModal
          setShowModal={setShowFormSubmissionsModal}
          showModal={showFormSubmissionsModal}
          formId={formId}
        />
      )}
      {showEditFormModal && (
        <CreateEditForm
          showAddFormModal={showEditFormModal}
          setShowAddFormModal={setShowEditFormModal}
          editMode={true}
          formId={formId}
        />
      )}
      {showTakeFormModal && (
        <TakeFormModal
          formId={formId}
          showTakeFormModal={showTakeFormModal}
          setShowTakeFormModal={setShowTakeFormModal}
        />
      )}
      {showViewSubmissionModal && (
        <ViewSubmissionModal
          formId={formId}
          showViewSubmissionModal={showViewSubmissionModal}
          setShowViewSubmissionModal={setShowViewSubmissionModal}
        />
      )}
      {showEditSectionModal && (
        <CreateEditSection
          showModal={showEditSectionModal}
          setShowModal={setShowEditSectionModal}
          editMode={true}
          sectionId={selectedSection?.id}
          initialData={selectedSection}
        />
      )}
      {showFiltersModal && (
        <FiltersModal
          setShowFiltersModal={setShowFiltersModal}
          showFiltersModal={showFiltersModal}
        />
      )}
    </>
  )
}

export default EmployeeForms
